// src/features/captions/CaptionsApi.ts

import { webApi } from '@/shared/api/WebApi';
import type {
  CaptionGenerateReq,
  CaptionGenerateRes,
  CaptionGenerationsRes,
  CaptionTermSetsRes,
  CaptionTermSetWriteRes,
  SubaccountCaptionGrantRes,
} from './types';

/** Customer-facing live-captions routes (/v1/captions + the sub-account grant). */
export class CaptionsApi {
  /** All term sets for the account: the account-scope set + media-stream sets. */
  static async getTermSets(cId: number): Promise<CaptionTermSetsRes> {
    return webApi.get<CaptionTermSetsRes>('captions/term-sets', { cId });
  }

  static async putAccountTermSet(cId: number, terms: string[]): Promise<CaptionTermSetWriteRes> {
    return webApi.put<CaptionTermSetWriteRes>('captions/term-sets/account', { cId }, { terms });
  }

  static async deleteAccountTermSet(cId: number): Promise<CaptionTermSetWriteRes> {
    return webApi.delete<CaptionTermSetWriteRes>('captions/term-sets/account', { cId });
  }

  static async putStreamTermSet(cId: number, mediaId: number, terms: string[]): Promise<CaptionTermSetWriteRes> {
    return webApi.put<CaptionTermSetWriteRes>(`captions/term-sets/media-stream/${mediaId}`, { cId }, { terms });
  }

  static async deleteStreamTermSet(cId: number, mediaId: number): Promise<CaptionTermSetWriteRes> {
    return webApi.delete<CaptionTermSetWriteRes>(`captions/term-sets/media-stream/${mediaId}`, { cId });
  }

  /**
   * AI term research. SYNCHRONOUS on the server and long-running (30–150 s) —
   * deliberately no AbortSignal/timeout here so the fetch rides the browser
   * default (≥300 s in practice; the contract requires tolerating ≥240 s).
   */
  static async generateTerms(cId: number, req: CaptionGenerateReq): Promise<CaptionGenerateRes> {
    return webApi.post<CaptionGenerateRes>('captions/term-sets/generate', { cId }, req);
  }

  /** Last 10 research runs (terms + rationale included — past drafts are re-openable). */
  static async getGenerations(cId: number): Promise<CaptionGenerationsRes> {
    return webApi.get<CaptionGenerationsRes>('captions/term-sets/generations', { cId });
  }

  /**
   * Parent grant of captions to a sub-account. CONTRACT EXCEPTION: no cId
   * query param — the parent account is derived from auth on the server.
   */
  static async setSubaccountCaptionGrant(childCId: number, allow: boolean): Promise<SubaccountCaptionGrantRes> {
    return webApi.post<SubaccountCaptionGrantRes>(`subaccount/${childCId}/captions`, undefined, { allow });
  }
}
